import ExtensionMessageType from './ExtensionMessageType';
import ISmimeAdapter from './ISmimeAdapter';
import onMissingExtension from '../actions/onMissingExtension';
import SmimeMessage from '../store/schema/SmimeMessage';
import noop from 'lodash-es/noop';
import SmimeType from '../store/schema/SmimeType';
import {
    OWA_PAGE_TO_SMIME_EXTENSION_EVENT_NAME,
    SMIME_EXTENSION_TO_OWA_PAGE_EVENT_NAME,
} from '../utils/constants';
import SmimeResult from '../store/schema/SmimeResult';

// Callback type for CustomEvent
type SendRequestCallbackType = (extensionResponse: string) => void;

// Type containing data, messageType and id, sent to S/MIME extension
type ModernExtensionRequest = {
    data: CustomEventRequest;
    messageType: string;
    requestId: number;
};

// Type containing data, messageType and id, received from S/MIME extension
type ModernExtensionResponse = {
    data: string;
    messageType: string;
    requestId: number;
};

// Type containing actual data to be sent to S/MIME extension
type CustomEventRequest = {
    emailMessage?: string;
    encryptionCertificates?: string;
    mime?: string;
    settings?: string;
    signingCertificate?: string;
    smime?: string;
    smimeType?: SmimeType;
    subjectKeyIdentifier?: string;
};

let requestId: number = 0;
const pendingCallbacks: Map<number, SendRequestCallbackType> = new Map<
    number,
    SendRequestCallbackType
>();

/**
 * Common callback handler for all CustomEvent raised, which is called on receiving CustomEvent response
 */
function onCallbackFromExtension(event: Event) {
    const modernExtensionResponse: ModernExtensionResponse = event['detail'];
    const callback = pendingCallbacks.get(modernExtensionResponse.requestId);
    callback(modernExtensionResponse.data.toString());
    pendingCallbacks.delete(modernExtensionResponse.requestId);
}

/**
 * Emits a CustomEvent of type messageType with data and callback
 * @param messageType string describing the custom event type to be sent to extension
 * @param data contains data in CustomEventRequest object properties, based on the messageType
 * @param callback is the function to be called when there is a response to the custom event
 */
function sendRequest(
    messageType: string,
    data: CustomEventRequest,
    callback: SendRequestCallbackType
) {
    ++requestId;
    const modernExtensionRequest: ModernExtensionRequest = {
        messageType,
        data,
        requestId,
    };

    // Create a CustomEvent with payload(detail) of type ModernExtensionRequest
    const customEvent = new CustomEvent<ModernExtensionRequest>(
        OWA_PAGE_TO_SMIME_EXTENSION_EVENT_NAME,
        {
            detail: modernExtensionRequest,
        }
    );
    pendingCallbacks.set(requestId, callback);
    document.dispatchEvent(customEvent);
}

/**
 * Returns an object containing API methods which use CustomEvents to communicate with S/MIME extension.
 */
const ModernAdapter: ISmimeAdapter = {
    initializeAsync: (settings: string): Promise<string> => {
        // Register a callback which will listen to event emitted from the S/MIME extension
        document.addEventListener(
            SMIME_EXTENSION_TO_OWA_PAGE_EVENT_NAME,
            onCallbackFromExtension,
            true /* useCapture */
        );
        return new Promise(resolve => {
            let isExtensionFound = false;
            sendRequest(ExtensionMessageType.GetExtensionVersion, null /* data */, resultJson => {
                isExtensionFound = JSON.parse(resultJson)['version'] ? true : false;
            });
            if (isExtensionFound) {
                sendRequest(ExtensionMessageType.Initialize, { settings }, resolve);
            } else {
                onMissingExtension();
                // We don't want to reject the promise because we have handled this case by calling action.
                resolve('');
            }
        });
    },

    createMessageFromSmimeAsync: (smime: string): Promise<SmimeResult> => {
        return new Promise(resolve => {
            sendRequest(
                ExtensionMessageType.CreateMessageFromSmime,
                { smime },
                (resultJSON: string) => resolve(JSON.parse(resultJSON))
            );
        });
    },

    createSmimeFromMessageAsync: (
        smimeMessage: SmimeMessage,
        encryptionCertificates: string[],
        signingCertificate: string
    ): Promise<string> => {
        const data: CustomEventRequest = {
            smimeType: smimeMessage.SmimeType,
            emailMessage: JSON.stringify(smimeMessage),
            encryptionCertificates: encryptionCertificates
                ? JSON.stringify(encryptionCertificates)
                : '',
            signingCertificate,
        };
        return new Promise(resolve => {
            sendRequest(ExtensionMessageType.CreateSmimeFromMessage, data, resolve);
        });
    },

    createMessageFromItemAttachmentAsync: (mime: string): Promise<SmimeResult> => {
        return new Promise(resolve => {
            sendRequest(
                ExtensionMessageType.CreateMessageFromItemAttachment,
                { mime },
                (resultJSON: string) => resolve(JSON.parse(resultJSON))
            );
        });
    },

    getSigningCertificateAsync: (subjectKeyIdentifier: string): Promise<SmimeResult> => {
        return new Promise(resolve => {
            sendRequest(
                ExtensionMessageType.GetSigningCertificate,
                { subjectKeyIdentifier },
                (resultJSON: string) => resolve(JSON.parse(resultJSON))
            );
        });
    },

    doesExtensionHaveBrowserOptionPage: (): boolean => {
        return true;
    },

    openExtensionOptionsPage: (): void => {
        sendRequest(ExtensionMessageType.ShowOptions, null /*data*/, noop /*callback*/);
    },

    disposeAsync: (): void => {
        sendRequest(ExtensionMessageType.Dispose, null, () => {
            // Remove the callback which will listen to event emitted from the S/MIME extension
            document.removeEventListener(
                SMIME_EXTENSION_TO_OWA_PAGE_EVENT_NAME,
                onCallbackFromExtension,
                true
            );
        });
    },
};

export default ModernAdapter;
